import { PaymentMethod } from "@/helpers/interfaces/organisations";
import {
    isWalletOnlyType,
    PaymentMethods,
    WalletOnlyType,
} from "@esenda/core-ui/constants";

export const supportsWalletType = (
    method: PaymentMethod | null | undefined,
    matcher: (type: string) => boolean,
): boolean =>
    method?.adyenPaymentOptions?.some(({ type }) => matcher(type)) === true;

export const supportsApplePay = (method: PaymentMethod | null | undefined) =>
    supportsWalletType(method, (type) => type === "applepay");

export const supportsGooglePay = (method: PaymentMethod | null | undefined) =>
    supportsWalletType(method, (type) => type === "googlepay");

export const supportsWeChatPay = (method: PaymentMethod | null | undefined) =>
    supportsWalletType(method, (type) => type === WalletOnlyType.WeChatPayWeb);

export const supportsAlipay = (method: PaymentMethod | null | undefined) =>
    supportsWalletType(method, (type) => type === WalletOnlyType.Alipay);

export const isWalletOnlyMethod = (
    method: PaymentMethod | null | undefined,
): boolean => {
    const options = method?.adyenPaymentOptions ?? [];
    return (
        options.length > 0 &&
        options.every(({ type }) => isWalletOnlyType(type))
    );
};

/**
 * True when the method is an Adyen method whose payment options are made up
 * exclusively of market-priority wallets (Alipay / WeChat Pay). These are
 * surfaced as a dedicated "WeChat / AliPay" card rather than a card form.
 */
export const isMarketPriorityWalletOnlyMethod = (
    method: PaymentMethod | null | undefined,
): boolean => {
    if (method?.paymentMethod !== PaymentMethods.ADYEN) {
        return false;
    }

    const options = method?.adyenPaymentOptions ?? [];
    return (
        options.length > 0 &&
        options.every(({ type }) => isWalletOnlyType(type ?? ""))
    );
};

/**
 * Returns the i18n key for a market-priority wallet-only method's display name,
 * adapting to whichever wallets the method actually supports.
 */
export const getWalletOnlyMethodNameKey = (
    method: PaymentMethod | null | undefined,
): string => {
    const hasWeChat = supportsWeChatPay(method);
    const hasAlipay = supportsAlipay(method);

    if (hasWeChat && hasAlipay) return "walletCard.wechatAndAlipay";
    if (hasWeChat) return "walletCard.wechat";
    if (hasAlipay) return "walletCard.alipay";
    return "walletCard.wechatAndAlipay";
};
